import {
    DataTable,
    DataTableSelectionMultipleChangeEvent,
    DataTableStateEvent
} from "primereact/datatable";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectDefect } from "../../store/selectors/defectSelectors";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    setFailed,
    setFilteredDefects,
    setFilters,
    setPage,
    setToDelete,
    updateItem
} from "../../store/slices/defectSlice";
import { defaultDefectColumns } from "../../utils/defectTableHelper";
import { DefectTableHeader } from "./DefectTableHeader";
import { cloneDeep } from "lodash";
import { ColumnEvent } from "primereact/column";
import { updateDefect } from "../../services/defectService";
import {
    selectInspectionData,
    selectInspectionEnableLowPriorityDefects,
    selectInspectionIsDisabled
} from "../../store/selectors/inspectionSelectors";
import { Defect } from "../../types/defect";
import { isDefaultDefectCode } from "../../utils/defectCodeHelper";
import {
    isIdConditionInspection,
    REGIME_MAPPINGS_RELEASE_FLAG
} from "../../utils/regimeMappingHelper";
import { fetchRegimeMappings } from "../../store/thunks/regimeMappingThunks";
import { useFlags } from "launchdarkly-react-client-sdk";

export const DefectTable = () => {
    const dispatch = useAppDispatch();

    const { data, loading, globalFilterValue, filters, page } =
        useAppSelector(selectDefect);
    const inspectionDisabled = useAppSelector(selectInspectionIsDisabled);
    const enableLowPriorityDefects = useAppSelector(
        selectInspectionEnableLowPriorityDefects
    );
    const inspection = useAppSelector(selectInspectionData);
    const flags = useFlags();
    const isRowSelectable = () => !inspectionDisabled;

    // The mappings only cover ID Condition inspections today (Pre-PSC is to
    // follow), so nothing is fetched for any other type.
    const showRegimeCodes =
        !!flags[REGIME_MAPPINGS_RELEASE_FLAG] &&
        isIdConditionInspection(inspection);

    useEffect(() => {
        if (showRegimeCodes) {
            dispatch(fetchRegimeMappings());
        }
    }, [showRegimeCodes, dispatch]);

    const [checkbox, setCheckbox] = useState<Defect[]>([]);

    const onPage = useCallback((event: DataTableStateEvent) => {
        dispatch(setPage({ row: event.rows, first: event.first }));
    }, []);

    const onFilter = useCallback((event: DataTableStateEvent) => {
        dispatch(setFilters(event.filters));
    }, []);

    const onValueChange = useCallback((value: Defect[]) => {
        dispatch(setFilteredDefects(value));
    }, []);

    const toggleSelection = useCallback(
        (event: DataTableSelectionMultipleChangeEvent<Defect[]>) => {
            setCheckbox(event.value);
            dispatch(setToDelete(event.value));
        },
        []
    );

    const onCellEditComplete = useCallback(async (event: ColumnEvent) => {
        const { newValue, newRowData, value } = event;

        if (newValue !== value) {
            dispatch(updateItem(newRowData));

            const updated = await updateDefect(newRowData);

            if (!updated) {
                dispatch(setFailed([newRowData.defectId]));
            }
        }
    }, []);

    const rowClassName = useCallback((data: Defect) => {
        return isDefaultDefectCode(data.typeCode) ? 'default-defect-code' : '';
    }, []);

    const renderHeader = useMemo(
        () => <DefectTableHeader />,
        [globalFilterValue]
    );

    const value = useMemo(() => cloneDeep(data), [data]);

    const columns = useMemo(
        () =>
            defaultDefectColumns(
                data,
                onCellEditComplete,
                inspectionDisabled,
                !!enableLowPriorityDefects,
                showRegimeCodes
            ),
        [data, showRegimeCodes]
    );

    const rowOptions = useMemo(() => [50, 75, 100, 150, 250], []);

    return (
        <DataTable
            value={value}
            dataKey="defectId"
            loading={loading}
            filters={filters}
            onFilter={onFilter}
            onValueChange={onValueChange}
            filterDisplay="row"
            editMode="cell"
            paginator
            scrollable
            removableSort
            stripedRows
            size="small"
            first={page.first}
            rows={page.row}
            rowsPerPageOptions={rowOptions}
            onPage={onPage}
            header={renderHeader}
            selectionMode="checkbox"
            selection={checkbox}
            onSelectionChange={toggleSelection}
            isDataSelectable={isRowSelectable}
            rowClassName={rowClassName}
        >
            {columns}
        </DataTable>
    );
};
